import {t} from "@lingui/core/macro";

import {RequestInfo} from "@khan/shared-core";
import {cssUrl} from "@khan/urls";

export type Supporter =
    | {
          heading?: string;
      }
    | string;

export type Section = {
    title?: string;
    subtitle?: string;
    columns?: Array<Array<Supporter>>;
    divider?: "default" | "image";
    url?: string;
};

export const BLANK = "";

const startDate = new Date("2023-07-01T00:00:00Z"); // July 1, 2023
const endDate = new Date("2024-06-30T23:59:59Z"); // June 30, 2024
const startDateText = Intl.DateTimeFormat(RequestInfo.kaLocale, {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
}).format(new Date(startDate));
const endDateText = Intl.DateTimeFormat(RequestInfo.kaLocale, {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
}).format(new Date(endDate));

// NOTE: this function is needed because calls to i18n.* must occur within a
// function
export const getSections = (): Array<Section> => [
    {
        title: t`Founding partners`,
        columns: [
            [
                "Ann and John Doerr",
                "Bill and Melinda Gates Foundation",
                "Google",
            ],
            ["Reed Hastings", "Fundação Lemann", "The O’Sullivan Foundation"],
            [
                "Fundación Carlos Slim",
                "Tata Trusts",
                "Valhalla Charitable Foundation",
            ],
        ],
    },
    {
        divider: "default",
    },
    {
        title: t`Lifetime giving`,
        columns: [
            [
                {heading: t`$10,000,000 and above`},
                "Anonymous (4)",
                "Amgen Foundation",
                "Bank of America",
                "Dan Benton",
                "Comcast",
                "Ann and John Doerr",
                "Bill and Melinda Gates Foundation",
                "Google",
                "Fundação Lemann",
                "Overdeck Family Foundation",
                "Carlos Rodriguez-Pastor and Gabriela Perez Rocchietti",
                "Valhalla Foundation",
                "Windsong Trust",
            ],
            [
                {heading: t`$5,000,000 to $9,999,999`},
                "AT&T Foundation",
                "Dalio Philanthropies",
                "Jack Dorsey",
                "ExxonMobil Foundation",
                "Bill Gates",
                "General Motors",
                "Imaginable Futures",
                "Elon Musk Foundation",
                "The O'Sullivan Foundation",
                "Siegel Family Endowment",
                "Fundación Carlos Slim",
                "Stand Together Trust",
                "Stripe",
                "Tata Trusts",
            ],
            [
                {heading: t`$1,000,000 to $4,999,999`},
                "Anonymous (3)",
                "Adobe",
                "Geraldine Acuña-Sunshine and Gabriel Sunshine",
                "J.A. and Kathryn Albertson Foundation",
                "Amplify",
                "Ross and Michele Annable",
                "Axis Bank Limited",
                "The Eli and Edythe Broad Foundation",
                "Capgemini Services Pvt. Ltd",
                "Carnegie Corporation of New York",
                "Arthur & Carlyse Ciocca Charitable Foundation",
                "Beth and Ravenel Curry Foundation",
                "East Texas Communities Foundation",
                "Diane Greene and Mendel Rosenblum",
                "Dick and Ethie Haworth",
                "HDFC Bank",
                "The Leona M. and Harry B. Helmsley Charitable Trust",
                "Jaquelin Hume Foundation",
                "Hyatt Hotels Foundation",
                "Intuit",
            ],
            [
                // BLANK will push down this column one row so that it's
                // aligned with the previous column which has a heading
                BLANK,
                "Robert Wood Johnson Foundation",
                "Kotak Mahindra Bank Limited",
                "Kung Guerra Foundation",
                "Learning Engineering Virtual Institute",
                "Mark and Debra Leslie",
                "Lozier Foundation",
                "MathWorks",
                "Craig and Susan McCaw Foundation",
                "Novartis Pharma AG",
                "Oracle",
                "Pousaz Philanthropies Foundation",
                "Tim and Caroline Reynolds",
                "Wendy and Eric Schmidt",
                "Skoll Foundation",
                "The Smith Family Foundation",
                "The Stiles-Nicholson Foundation",
                "The Tides Foundation",
                "Walmart",
                "The Walt Disney Company",
                "Yass Foundation for Education",
            ],
        ],
    },
    {
        divider: "image",
        url: cssUrl("/images/about/supporters/supporters-banner.jpg"),
    },
    {
        title: t`Annual Giving`,
        subtitle: t`This list reflects giving from ${startDateText} - ${endDateText}.`,
        columns: [
            [
                {heading: t`$1,000,000 and above`},
                "Anonymous",
                "Amgen Foundation",
                "Arthur & Carlyse Ciocca Charitable Foundation",
                "Beth and Ravenel Curry Foundation",
                "Ann and John Doerr",
                "Bill and Melinda Gates Foundation",
                "General Motors",
                "Dick and Ethie Haworth",
                "Learning Engineering Virtual Institute",
                "MathWorks",
                "Overdeck Family Foundation",
                "Carlos Rodriguez-Pastor and Gabriela Perez Rocchietti",
                "Siegel Family Endowment",
                "Stand Together Trust",
                "Windsong Trust",
                "Yass Foundation for Education",
            ],
            [
                {heading: t`$500,000 to $999,999`},
                "Anonymous",
                "Axis Bank Limited",
                "Capital One",
                "HDFC Bank",
                "Fundação Lemann",
                "Pousaz Philanthropies Foundation",
                "The Smith Family Foundation",
                "State Bank of India (SBI) Foundation",
            ],
            [
                {heading: t`$250,000 to $499,999`},
                "Anonymous",
                "Capgemini Services Pvt. Ltd",
                "Cognizant Foundation",
                "ExxonMobil",
                "Heinz Family Foundation",
                "Intel",
                "Kotak Mahindra Bank Limited",
                "Kung Guerra Foundation",
                "Mark and Debra Leslie",
                "Lozier Foundation",
                "Jean Perkins Foundation",
                "Telles Foundation",
                "U.S. Chamber of Commerce Foundation",
            ],
            [
                {heading: t`$100,000 to $249,000`},
                "Concentrix",
                "John and Eileen Donahoe",
                "Hettinger Foundation",
                "IndiaMART Intermesh Limited",
                "Intuitive Foundation",
                "The McCance Foundation",
                "Dionne and Francis Najafi",
                "Open AI",
                "Palo Alto Networks",
                "The Reddy Family Fund",
                "Adam R. Scripps Foundation",
            ],
        ],
    },
    {
        divider: "default",
    },
    {
        title: t`Annual Learners Fund Members`,
        columns: [
            [
                {heading: t`$50,000-$99,999`},
                "Aall Foundation",
                "Brock Family Community Foundation",
                "The Cinelli Family Foundation",
                "Corbett Family Charitable Foundation",
                "Coxe Family Fund",
                "Feathercatcher Fund",
                "InfoEdge",
                "Qatalyst Partners",
                "Rampell Family Fund",
                "Dave and Kathy Robertson",
                "Shannon Schlafly",
                "Peter-Pike and Meg Sloan",
            ],
            [
                {heading: t`$25,000-$49,999`},
                "Anonymous",
                "Shahid Ahmed and Padma Ramaswamy",
                "The Carroll Obremskey Charitable Fund",
                "CG Tech",
                "Ray and Barbara Dalio",
                "eCommerce Fuel",
                "Episource Pharma P Ltd",
                "Curtis Feeny",
                "Allen Liu",
                "James and Sarah Manyika",
            ],
            [
                // BLANK will push down this column one row so that it's
                // aligned with the previous column which has a heading
                BLANK,
                "MSB Cockayne Fund Inc.",
                "Deborah Quazzo",
                "Amy Rao and Harry Plant",
                "Rothrock Family Fund",
                "Sulentic Family Foundation",
                "Tesinshky Family Foundation",
                "Thomas Family Foundation",
                "William and Kimberly Witchger",
            ],
        ],
    },
    {
        divider: "default",
    },
    {
        title: t`In-kind support`,
        columns: [
            [
                "Adobe Systems Incorporated",
                "Bain & Company",
                "Covington & Burling LLP",
                "Fastly",
            ],
            ["Google Inc", "Microsoft", "SOSV"],
            [
                "Waggener Edstrom Worldwide Inc",
                "Walmart",
                "Wilson Sonsini Goodrich & Rosati",
            ],
        ],
    },
    {
        divider: "default",
    },
];
